/**
 * Shared types for document filter infrastructure
 * Used by nmed-doc-search-filter-store, projects-store, and saved-documents-store
 */

/* ---------------------------------------------------------------------------------------------- */
/*                                       RANGE FILTER                                             */
/* ---------------------------------------------------------------------------------------------- */

/**
 * A range filter for date or numeric ranges.
 * Matches rows where the field value falls between start_at and end_at (inclusive).
 */
export interface RangeFilter {
   type: 'range';
   start_at: string | number;
   end_at: string | number;
}

/**
 * Search key item - either a plain string or a range filter object
 */
export type SearchKeyItem = string | RangeFilter;

/**
 * Type guard to check if a search key is a RangeFilter
 */
export function isRangeFilter(key: SearchKeyItem): key is RangeFilter {
   return typeof key === 'object' && key !== null && key.type === 'range';
}

/* ---------------------------------------------------------------------------------------------- */
/*                                       YEAR FILTER                                              */
/* ---------------------------------------------------------------------------------------------- */

/**
 * Year filter state - supports single year or year range
 */
export interface YearFilterState {
   type: 'single' | 'range';
   year: string;
   startYear: string;
   endYear: string;
}

/**
 * Creates an empty year filter state
 */
export function createEmptyYearFilter(): YearFilterState {
   return {
      type: 'single',
      year: '',
      startYear: '',
      endYear: '',
   };
}

/* ---------------------------------------------------------------------------------------------- */
/*                                      SEARCH KEYWORD                                            */
/* ---------------------------------------------------------------------------------------------- */

/**
 * Grouped search filters structure
 * Example: { 'County': ['Bernalillo', 'Santa Fe'], 'FacilityName': ['Plant A'] }
 * Date ranges are stored as RangeFilter objects: { 'ExpiryDate': [{ type: 'range', start_at: '...', end_at: '...' }] }
 */
export interface SearchKeywordState {
   filters: Record<string, SearchKeyItem[]>;
}

/**
 * Creates an empty search keyword state
 */
export function createEmptySearchKeywordState(): SearchKeywordState {
   return {
      filters: {},
   };
}

/* ---------------------------------------------------------------------------------------------- */
/*                                       SORT CONFIG                                              */
/* ---------------------------------------------------------------------------------------------- */

/**
 * Sort order values (lowercase, for API)
 */
export type SortOrder = 'asc' | 'desc' | '';

/**
 * Sort direction values (uppercase, for UI/table headers)
 */
export type SortDirection = 'ASC' | 'DESC';

/**
 * Sort configuration
 */
export interface SortConfig {
   field: string;
   direction: SortDirection;
}

/**
 * Creates an empty sort config
 */
export function createEmptySortConfig(): SortConfig {
   return {
      field: '',
      direction: 'ASC',
   };
}

/* ---------------------------------------------------------------------------------------------- */
/*                                       FILTER STATE                                             */
/* ---------------------------------------------------------------------------------------------- */

/**
 * Filter state for bureau/document type filters (always multi-select)
 */
export interface FilterState {
   bureaus: string[];
   documentTypes: string[];
   searchKeyword: SearchKeywordState;
   globalSearchKeyword: string;
   year: YearFilterState;
   sortField: string;
   sortOrder: SortOrder;
}

/**
 * Creates an empty filter state
 */
export function createEmptyFilterState(): FilterState {
   return {
      bureaus: [],
      documentTypes: [],
      searchKeyword: createEmptySearchKeywordState(),
      globalSearchKeyword: '',
      year: createEmptyYearFilter(),
      sortField: '',
      sortOrder: '',
   };
}

/**
 * @deprecated Use FilterState instead
 */
export type MultiSelectFilterState = FilterState;

/**
 * @deprecated Use createEmptyFilterState instead
 */
export const createEmptyMultiSelectFilterState = createEmptyFilterState;

/* ---------------------------------------------------------------------------------------------- */
/*                                     PAGINATION STATE                                           */
/* ---------------------------------------------------------------------------------------------- */

/**
 * Pagination state
 */
export interface PaginationState {
   currentPage: number;
   itemsPerPage: number;
   totalCount: number;
   totalPages: number;
   filteredCount: number;
}

/**
 * Creates an empty pagination state
 */
export function createEmptyPaginationState(itemsPerPage: number = 30): PaginationState {
   return {
      currentPage: 1,
      itemsPerPage,
      totalCount: 0,
      totalPages: 0,
      filteredCount: 0,
   };
}

/* ---------------------------------------------------------------------------------------------- */
/*                                   DOCUMENT FETCH PARAMS                                        */
/* ---------------------------------------------------------------------------------------------- */

/**
 * Parameters for fetching documents from the NMED API
 */
export interface DocumentFetchParams {
   apiKey: string;
   /** Document IDs to fetch (for projects/saved-docs) */
   docIds?: string[];
   /** Comma-separated bureau names (for doc-search) or single bureau */
   bureau: string;
   /** Comma-separated doc type names (for doc-search) or single doc type */
   docType: string;
   /** Year filter (single year) */
   year: string;
   /** Year range start */
   yearFrom: string;
   /** Year range end */
   yearTo: string;
   /** Search field names (parallel array with searchKeys) */
   searchFieldsList: string[];
   /** Search values (parallel array with searchFields) - can be strings or RangeFilter objects */
   searchKeysList: SearchKeyItem[];
   /** Global search keyword */
   globalSearchKey: string;
   /** Pagination: starting row (0-indexed) */
   firstRow: number;
   /** Pagination: number of rows to fetch */
   numberOfRows: number;
   /** Sort field name */
   sortField: string;
   /** Sort order ('asc' | 'desc' | '') */
   sortOrder: SortOrder;
   /** Optional: target latitude for location-based filtering */
   targetLat?: string;
   /** Optional: target longitude for location-based filtering */
   targetLng?: string;
   /** Optional: radius in miles for location-based filtering */
   radius?: number;
}

/* ---------------------------------------------------------------------------------------------- */
/*                                       API RETRY CONFIG                                         */
/* ---------------------------------------------------------------------------------------------- */

/**
 * Standard API retry configuration
 */
export const API_RETRY_CONFIG = {
   retries: 3,
   retryDelay: 100,
   retryStatusCodes: [408, 429, 504, 500],
} as const;

/**
 * Extended API retry configuration (for doc-search)
 */
export const API_RETRY_CONFIG_EXTENDED = {
   retries: 5,
   retryDelay: 100,
   retryStatusCodes: [408, 429, 504, 500],
} as const;
